import { defaultFontStyles, FontStylesType } from '@dynamic/ui';
import React, { forwardRef, useEffect, useState } from 'react';
import styled from 'styled-components';

import { ThemeConfig } from '#/service/config/config.types';
import { ButtonShape, ButtonVariant } from '#/service/constants';
import { pascal } from '#/service/utils';

import { Spinner, SpinnerThickness } from '../Spinner/Spinner';
import { focusShadow } from '../styles';

const BORDER_WIDTH_PX = 2;

interface StyledProps {
  theme: ThemeConfig;
  $variant: ButtonVariant;
}

export interface BaseButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  $variant: ButtonVariant;
}

const buttonShapeRadius: Record<ButtonShape, string> = {
  [ButtonShape.Pill]: '100rem',
  [ButtonShape.Squared]: '0',
  [ButtonShape.Rounded]: '0.44rem',
};

export const getBorderRadius = ({ theme, $variant }: StyledProps) => {
  const variantConfig = $variant?.toLowerCase();
  const shape = theme.button?.[variantConfig]?.shape ?? ButtonShape.Pill;

  if (!Object.values(ButtonVariant).includes($variant)) {
    console.warn('Invalid button variant:', $variant);
  }

  return `border-radius: ${buttonShapeRadius[shape]};`;
};

const getSharedStyle = (variant: string, state = '') => {
  return `
    background: var(--button${variant}BackgroundLinearGradient${state}) border-box;

    &::before {
      content: '';
      position: absolute;
      inset: ${-BORDER_WIDTH_PX}px;
      border: inherit;
      border-radius: inherit;
      background: var(--button${variant}BorderLinearGradient${state}) border-box;
      mask:
        linear-gradient(#fff) padding-box exclude,
        linear-gradient(#fff) border-box;
    }
  `;
};

export const getFontWeight = ({ $variant, theme }: StyledProps) => {
  const variant = $variant?.toLowerCase();

  // If the variant is ghost, we don't want to apply any font weight styles.
  if (variant === 'ghost') {
    return '';
  }
  const variantFontWeight =
    theme.button?.[variant]?.fontWeight ?? FontStylesType.Regular;
  const fontWeight = defaultFontStyles[variantFontWeight].weight;

  // using && to increase specificity, especially for buttons that don't have the text in a span.
  return `
    &&, && > span {
      font-weight: ${fontWeight};
    }
  `;
};

export const getVariantStyle = (props: StyledProps) => {
  const { $variant } = props;
  const variant = pascal($variant);

  if ($variant === ButtonVariant.Ghost) {
    return `
      color: var(--buttonPrimaryForeground);
      background: transparent;
      border: none;
    `;
  }

  return `
    color: var(--button${variant}Foreground);
    border: ${BORDER_WIDTH_PX}px solid transparent;  

    ${getSharedStyle(variant)}
    &:hover:enabled {
      ${getSharedStyle(variant, 'Hover')}
    }
  `;
};

const getDisabledStyle = (props: StyledProps) => {
  const { $variant } = props;
  const variant = pascal($variant);

  return `
    cursor: default;
    ${getSharedStyle(variant, 'Disabled')}
  `;
};

const getHoverStyle = (props: StyledProps) => {
  const { $variant } = props;
  const variant = pascal($variant);

  return getSharedStyle(variant, 'Hover');
};

export const BaseButton = styled.button<BaseButtonProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  outline: none;
  font-size: inherit;
  line-height: 1rem;
  word-break: break-word;
  padding: 0.5rem 2rem;
  cursor: pointer;

  &:focus-visible {
    ${focusShadow}
  }

  &:hover:enabled {
    ${getHoverStyle}
  }

  ${getVariantStyle}
  ${getBorderRadius}
  ${getFontWeight}
  
  ${({ disabled }) => disabled && getDisabledStyle}

  &[data-full-width='true'] {
    width: 100%;
    text-align: center;
    display: block;
  }
`;

BaseButton.displayName = 'BaseButton';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual type of the button @default ButtonVariant.Primary */
  variant?: ButtonVariant;

  /** Adds a spinner on top of the button to signal awaiting response @default false */
  isLoading?: boolean;

  /** Custom text that will appear alongside the loading spinner. By default, only spinner will be shown @default '' */
  loadingText?: string;

  /** Prop that will make the button to use full width available and assure that text is centered @default false  */
  isFullWidth?: boolean;
}

/** Basic button component */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = ButtonVariant.Primary,
      isLoading = false,
      children = null,
      loadingText = '',
      type = 'button',
      isFullWidth = false,
      disabled,
      ...rest
    },
    ref
  ) => {
    const [isDisabled, setIsDisabled] = useState(disabled || isLoading);

    /** For accessibility purposes (focus-visible) we need to keep `isDisabled` in
     * component's state, to make sure it's re-rendered and placed correctly in tab order when
     * dynamically switching from disabled to enabled. Please do not remove that unless necessary. */
    useEffect(() => {
      setIsDisabled(disabled || isLoading);
    }, [disabled, isLoading]);

    return (
      <BaseButton
        ref={ref}
        type={type}
        {...rest}
        $variant={variant}
        data-full-width={isFullWidth}
        disabled={isDisabled}
      >
        {isLoading ? (
          <>
            <StyledSpinner
              animationDuration="0.7s"
              thickness={SpinnerThickness.Bold}
            />
            {loadingText}
          </>
        ) : (
          children
        )}
      </BaseButton>
    );
  }
);

Button.displayName = 'Button';

const StyledSpinner = styled(Spinner)`
  margin-right: 0.2rem;
`;
